import React from 'react';
import { useHistory } from 'react-router-dom';

import {
    WeezHeader,
    Button,
    CurrencyInputLabel,
    HeaderCard,
    Message,
    TitleCard,
    ToastContext,
    TooltipHelp,
    Card,
    Select,
    ToggleContainer,
    ContentCard,
    Form,
    RadioLabel,
    Section
} from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { InternalResaleRateSettingsModel } from '../../../models';
import { useForm } from '../../../utils/validators';
import RateModel from '../../../models/rateModel';
import i18n from 'i18next';
import RateFeesEditor from '../../../components/rates/rate_fees/rateFeesEditor';
import Styles from './resaleRateSettings.module.css';
import getFormattedPrice from '../../../utils/currency';
import ConfirmModal from '../../../components/confirmModal';
import { fetchCommission } from '../../../utils/commission';
import PageError from '../../error/pageError';
import LoadingCard from '../../../components/loadingCard';

export default function ResaleRateSettings({ organization, event, rateId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [loadingError, setLoadingError] = React.useState(null);
    const locale = i18n.language;

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm({
        min_price: 0,
        max_price: 0,
        resale_rate: parseInt(rateId),
        resaler_fee: [],
        activated: false
    });

    const [settings, loadingSettings] = InternalResaleRateSettingsModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            rateId: rateId,
            allow_cache: false,
            onSuccess: settings => {
                setFormObject({
                    min_price: settings.min_price || null,
                    max_price: settings.max_price || null,
                    resale_rate: settings.resale_rate || null,
                    resaler_fee: settings.resaler_fee || [],
                    activated: settings.activated || false
                });
                setDisplayFees(settings.resaler_fee.length > 0);
            },
            onError: error => {
                if (error.code !== 404) setLoadingError(error);
            }
        },
        [organization.id, event.id, rateId]
    );

    const [displayFees, setDisplayFees] = React.useState(settings?.resaler_fee.length > 0);

    const [rate, setRate] = React.useState(null);
    const [rates, loadingRates] = RateModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            onSuccess: rates => {
                setRate(rates.find(rate => rate.id === parseInt(rateId)));
            }
        },
        [organization.id, event.id, rateId]
    );
    const rateOptions = React.useMemo(() => {
        if (!rates) return [];
        return rates.map(rate => ({
            key: rate.pk,
            text: rate.name,
            value: rate.pk
        }));
    }, [rates]);

    const [searchTerm, setSearchTerm] = React.useState('');
    const filteredRateOptions = React.useMemo(() => {
        if (!searchTerm) return rateOptions;
        return rateOptions.filter(option => new RegExp(searchTerm, 'gi').test(option.text));
    }, [rateOptions, searchTerm]);

    const [cancelTicketsOnDeactivation, setCancelTicketsOnDeactivation] = React.useState(false);
    const [configDeactivationModalShow, setConfigDeactivationModalShow] = React.useState(false);

    const resaleRate = React.useMemo(() => {
        if (!form.resale_rate || !rates) return null;
        return rates.find(rate => rate.id === form.resale_rate);
    }, [event, form, rates]);

    const [commission, setCommission] = React.useState(null);
    React.useEffect(() => {
        if (!resaleRate) {
            setCommission(null);
            return;
        }
        const taxID = resaleRate.tax_id || event?.tax_id;
        fetchCommission(resaleRate.price, organization.id, event.id, taxID, resaleRate.id)
            .then(response => setCommission(response.response.commission))
            .catch(e => {
                setCommission(null);
                toast.error(t(`ticket.rates.infos.commissionError`));
            });
    }, [resaleRate]);

    function onModalClose() {
        setConfigDeactivationModalShow(false);
        setCancelTicketsOnDeactivation(false);
    }

    function onClear() {
        setForm('resale_rate', null);
        setSearchTerm('');
    }

    const performSave = React.useCallback(
        async ({ shouldRedirect = true, activated = form.activated }) => {
            setSubmitting(true);
            try {
                if (cancelTicketsOnDeactivation) {
                    await InternalResaleRateSettingsModel.cancelResaleTickets({
                        organizationId: organization.id,
                        eventId: event.id,
                        rateId: rateId
                    });
                    setCancelTicketsOnDeactivation(false);
                }

                const method = settings ? 'patch' : 'post';
                const data = { ...form, activated };
                await InternalResaleRateSettingsModel.save(data, method, {
                    organizationId: organization.id,
                    eventId: event.id,
                    rateId: rateId
                });
                toast.success(t('ticket.resales.settings.toast.success'));
                if (shouldRedirect) history.push(`/ticket/O${organization.id}/E${event.id}/resale/activation`);
            } catch (error) {
                console.error('[RESALE RATE SETTINGS] Save error:', error);
                toast.error(t('ticket.resales.settings.toast.error'));
            } finally {
                setSubmitting(false);
                setConfigDeactivationModalShow(false);
            }
        },
        [form, organization.id, event.id, rateId, settings, cancelTicketsOnDeactivation, history]
    );

    const handleToggle = React.useCallback(
        checked => {
            setForm('activated', checked);

            if (!formValidate()) return;

            if (checked === false) {
                setConfigDeactivationModalShow(true);
                return;
            }

            performSave({ shouldRedirect: false, activated: checked });
        },
        [formValidate, performSave]
    );

    if (loadingError) return <PageError error={loadingError}></PageError>;

    return (
        <>
            <WeezHeader
                title={rate?.name || rateId}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/E${event.id}/resale/activation`) }}
                rightComponent={[
                    <Button
                        key="save"
                        primary
                        label={t('ticket.common.action.save')}
                        onClick={performSave}
                        disabled={loadingSettings || isSubmitting || Object.keys(formErrors).length > 0 || (!settings && !form?.activated) || loadingRates || !rate}
                    />
                ]}
            />

            <Card className={Styles['resale-rate-settings']}>
                <HeaderCard>
                    <TitleCard>{t('ticket.resales.rate_settings.title')}</TitleCard>
                </HeaderCard>
                <ContentCard>
                    {loadingSettings ? (
                        <LoadingCard loading={loadingSettings} />
                    ) : (
                        <Form>
                            <ToggleContainer
                                inlinePosition="left"
                                title={t('ticket.resales.rate_settings.activate')}
                                toggle={form.activated}
                                onToggle={checked => handleToggle(checked)}
                            />
                            <ConfirmModal
                                size="large"
                                labelTitle={t('ticket.resales.rate_settings.modal_title')}
                                labelButton={t('ticket.common.action.continue')}
                                labelCancel={t('ticket.common.action.cancel')}
                                onClose={onModalClose}
                                onAccept={() => performSave({ shouldRedirect: false })}
                                objectToSave={configDeactivationModalShow}
                                isSubmitting={isSubmitting}
                                className={Styles['purge-tickets-modal']}
                                labelBody={
                                    <>
                                        <Message type="warning" title={t('ticket.resales.rate_settings.modal_alert_title')}>
                                            {t('ticket.resales.rate_settings.modal_alert_description')}
                                        </Message>
                                        <p>{t('ticket.resales.rate_settings.modal_description')}</p>
                                        <RadioLabel
                                            name="keep_tickets"
                                            value="keep_tickets"
                                            checked={cancelTicketsOnDeactivation === false}
                                            inline
                                            bordered
                                            onChange={(e, { checked }) => {
                                                setCancelTicketsOnDeactivation(!checked);
                                            }}
                                        >
                                            {t('ticket.resales.rate_settings.modal_keep_tickets')}
                                        </RadioLabel>
                                        <RadioLabel
                                            name="cancel_tickets"
                                            value="cancel_tickets"
                                            checked={cancelTicketsOnDeactivation === true}
                                            inline
                                            bordered
                                            onChange={(e, { checked }) => {
                                                setCancelTicketsOnDeactivation(checked);
                                            }}
                                        >
                                            {t('ticket.resales.rate_settings.modal_cancel_tickets')}
                                        </RadioLabel>
                                    </>
                                }
                            />

                            {(form.activated || settings) && (
                                <>
                                    <CurrencyInputLabel
                                        label={
                                            <>
                                                {t('ticket.resales.rate_settings.price')}
                                                <TooltipHelp title={t('ticket.resales.rate_settings.price_tooltip')} />
                                                <span className="wz-input-sublabel">
                                                    {t('ticket.resales.rate_settings.price_original', { price: getFormattedPrice(rate?.price, event?.currency) })}
                                                </span>
                                            </>
                                        }
                                        currency={event?.currency}
                                        max={10000}
                                        value={form.min_price}
                                        onChange={({ value }) => setFormObject({ ...form, min_price: value, max_price: value })}
                                        locale={locale}
                                    />

                                    <ToggleContainer
                                        inlinePosition="left"
                                        title={t('ticket.resales.rate_settings.fees')}
                                        subtitle={t('ticket.resales.rate_settings.fees_subtitle')}
                                        toggle={displayFees}
                                        onToggle={checked => {
                                            setDisplayFees(checked);
                                            if (!checked) {
                                                setForm('resaler_fee', []);
                                            }
                                        }}
                                    >
                                        <Section>
                                            <RateFeesEditor
                                                fees={form.resaler_fee}
                                                event={event}
                                                onChange={value => setForm('resaler_fee', value)}
                                                setForm={setForm}
                                                formFieldKey="resaler_fee"
                                                titleKey="ticket.resales.rate_settings.fees_description"
                                            />
                                        </Section>
                                    </ToggleContainer>

                                    <Form.Field>
                                        <div className={Styles['label-with-tooltip']}>
                                            <Form.Label label={t('ticket.resales.rate_settings.resale_rate')} />
                                            <TooltipHelp title={t('ticket.resales.rate_settings.resale_rate_tooltip')} />
                                        </div>
                                        <Select
                                            placeholder={t('ticket.resales.rate_settings.resale_rate_placeholder')}
                                            options={filteredRateOptions}
                                            value={form.resale_rate}
                                            onChange={value => setForm('resale_rate', value)}
                                            onInputChange={({ target: { value } }) => setSearchTerm(value)}
                                            onClear={onClear}
                                            loading={loadingRates}
                                            search
                                            useNew
                                        />
                                        {commission && (
                                            <p className="wz-input-sublabel">
                                                {t('ticket.resales.rate_settings.new_commission', { price: getFormattedPrice(commission, event?.currency) })}
                                            </p>
                                        )}
                                    </Form.Field>
                                </>
                            )}
                        </Form>
                    )}
                </ContentCard>
            </Card>
        </>
    );
}
